import React, { useCallback, useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, Field, Input, Select, Textarea, BtnPrimary, BtnSecondary, Empty,
} from "@/components/primitives";
import { toast } from "sonner";
import {
  Envelope, ShieldCheck, PaperPlaneTilt, Clock, CheckCircle, XCircle, Megaphone, X,
  Function as FunctionIcon, Moon, Sun, IdentificationCard, Database, Truck as TruckIcon,
  Package as PackageIcon, MapPin, Tag as TagIcon, Buildings, Wrench, UsersThree,
} from "@phosphor-icons/react";
import { PayrollFormulasPanel } from "@/pages/PayrollFormulas";
import CrushProductsPanel from "@/components/CrushProductsPanel";
import LookupsPanel from "@/components/LookupsPanel";
import { useTheme } from "@/lib/theme";

const TABS = [
  { key: "email", label: "Email (SMTP)", icon: Envelope, adminOnly: true },
  { key: "brand", label: "Brand & Marketing", icon: Megaphone, adminOnly: true },
  { key: "formulas", label: "Payroll Formulas", icon: FunctionIcon, adminFinance: true },
  { key: "products", label: "Crush Products", icon: PackageIcon, adminOnly: true },
  { key: "cost_centers", label: "Cost Centers", icon: MapPin, adminOnly: true },
  { key: "cost_categories", label: "Cost Categories", icon: TagIcon, adminOnly: true },
  { key: "departments", label: "Departments", icon: Buildings, adminOnly: true },
  { key: "service_categories", label: "Service Categories", icon: Wrench, adminOnly: true },
  { key: "data", label: "Data Utilities", icon: Database, adminOnly: true },
  { key: "appearance", label: "Appearance", icon: Moon },
];

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const isFinance = user.role === "finance";
  const canFormulas = isAdmin || isFinance;

  const visibleTabs = TABS.filter((t) => {
    if (t.adminOnly) return isAdmin;
    if (t.adminFinance) return canFormulas;
    return true;
  });

  const [tab, setTab] = useState(visibleTabs[0]?.key || "appearance");
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [logs, setLogs] = useState([]);
  const [brand, setBrand] = useState(null);
  const [brandSaving, setBrandSaving] = useState(false);
  const [industryDraft, setIndustryDraft] = useState("");
  const { isDark, setMode } = useTheme();
  const [damstraSync, setDamstraSync] = useState(null);
  const [damstraSyncing, setDamstraSyncing] = useState(false);
  const [tonnageSync, setTonnageSync] = useState(null);
  const [tonnageSyncing, setTonnageSyncing] = useState(false);
  const [customerSync, setCustomerSync] = useState(null);
  const [customerSyncing, setCustomerSyncing] = useState(false);
  const [lookupSync, setLookupSync] = useState(null);
  const [lookupSyncing, setLookupSyncing] = useState(false);

  async function syncDamstraIds() {
    setDamstraSyncing(true);
    try {
      const { data } = await api.post("/admin/import-damstra-ids");
      setDamstraSync(data);
      toast.success(`Damstra sync: ${data.employees_updated} employees, ${data.payroll_rows_updated} payroll rows updated`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setDamstraSyncing(false);
    }
  }

  async function syncTonnage() {
    setTonnageSyncing(true);
    try {
      const { data } = await api.post("/admin/import-tonnage");
      setTonnageSync(data);
      toast.success(`Tonnage sync: ${data.inserted} rows inserted (replaced ${data.removed_previous_import} previous)`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setTonnageSyncing(false);
    }
  }

  async function syncCustomers() {
    setCustomerSyncing(true);
    try {
      const { data } = await api.post("/admin/import-customers");
      setCustomerSync(data);
      toast.success(`Customers: ${data.inserted} new, ${data.skipped_existing} already existed`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setCustomerSyncing(false); }
  }

  async function syncLookups() {
    setLookupSyncing(true);
    try {
      const { data } = await api.post("/admin/import-lookups");
      setLookupSync(data);
      toast.success(`Lookups: ${data.inserted} new entries across cost centers, categories, departments and service types`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLookupSyncing(false); }
  }

  const load = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/settings/email");
      setS(data);
    } catch (e) { toast.error(formatError(e)); }
  }, [isAdmin]);
  const loadLogs = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/settings/email/logs?limit=30");
      setLogs(data);
    } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
  }, [isAdmin]);
  const loadBrand = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/settings/brand");
      setBrand(data);
    } catch (e) { toast.error(formatError(e)); }
  }, [isAdmin]);
  useEffect(() => { load(); loadLogs(); loadBrand(); }, [load, loadLogs, loadBrand]);

  if (!canFormulas && !isAdmin) {
    return (
      <div data-testid="settings-page">
        <PageHeader overline="Admin" title="Settings" subtitle="Admin-only configuration" />
        <Empty label="Only admin, finance, or configured users can access settings." />
      </div>
    );
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      // Don't send the masked password back unchanged
      const payload = { ...s };
      if (payload.smtp_password?.startsWith("•")) delete payload.smtp_password;
      delete payload.has_password;
      const { data } = await api.put("/settings/email", payload);
      setS(data);
      toast.success("Email settings saved");
      loadLogs();
    } catch (e2) { toast.error(formatError(e2)); }
    finally { setSaving(false); }
  }

  async function sendTest() {
    setTesting(true);
    try {
      await api.post("/settings/email/test");
      toast.success(`Test email sent to ${user.email}`);
      loadLogs();
    } catch (e) { toast.error(formatError(e)); }
    finally { setTesting(false); }
  }

  if (isAdmin && !s && tab === "email") return <div className="text-sm text-[#52525b]">Loading settings…</div>;

  return (
    <div data-testid="settings-page" className="space-y-6">
      <PageHeader
        overline="Admin"
        title="Settings"
        subtitle="Configure email, brand, payroll formulas and appearance."
      />

      {/* Tab bar */}
      <div className="phizo-card p-1 inline-flex flex-wrap gap-1" role="tablist">
        {visibleTabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`settings-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold transition-colors ${active ? "bg-[#09090b] text-white" : "text-[#52525b] hover:bg-[#f4f4f5]"}`}
            >
              <Icon size={14} weight="bold" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "email" && isAdmin && s && (
      <div className="phizo-card p-6" data-testid="settings-email-card">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 bg-[#002FA7] text-white flex items-center justify-center">
            <Envelope size={18} weight="bold" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-black tracking-tight">Email (SMTP)</h2>
            <p className="text-xs text-[#52525b] mt-0.5">
              Used for Delivery Notes, Invoices and all outgoing system notifications.
            </p>
          </div>
        </div>

        <form onSubmit={save} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="From — Sender name">
              <Input value={s.sender_name || ""} data-testid="setting-sender-name"
                onChange={(e) => setS({ ...s, sender_name: e.target.value })}
                placeholder="Phizo Quarry" />
            </Field>
            <Field label="From — Sender email" hint="What recipients see as the sender">
              <Input type="email" value={s.sender_email || ""} data-testid="setting-sender-email"
                onChange={(e) => setS({ ...s, sender_email: e.target.value })}
                placeholder="team@phizoquarry.com" />
            </Field>
            <Field label="Master CC email" hint="Auto-CC'd on every outgoing email — leave blank to disable.">
              <Input type="email" value={s.master_cc_email || ""} data-testid="setting-master-cc"
                onChange={(e) => setS({ ...s, master_cc_email: e.target.value })}
                placeholder="team@phizoquarry.com" />
            </Field>
            <Field label="Provider">
              <Select value="gmail" disabled>
                <option value="gmail">Gmail / Google Workspace SMTP</option>
              </Select>
            </Field>
          </div>

          <div className="pt-3 mt-3 border-t border-[#e4e4e7]">
            <div className="phizo-label mb-3 flex items-center gap-2">
              <ShieldCheck size={14} weight="bold" /> Authentication
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="SMTP host">
                <Input value={s.smtp_host || ""} data-testid="setting-smtp-host"
                  onChange={(e) => setS({ ...s, smtp_host: e.target.value })} />
              </Field>
              <Field label="SMTP port">
                <Input type="number" value={s.smtp_port || 587} data-testid="setting-smtp-port"
                  onChange={(e) => setS({ ...s, smtp_port: Number(e.target.value) })} />
              </Field>
              <Field label="Authenticated mailbox (SMTP username)"
                hint="The actual Gmail / Workspace mailbox you generated the App Password for.">
                <Input type="email" value={s.smtp_username || ""} data-testid="setting-smtp-username"
                  onChange={(e) => setS({ ...s, smtp_username: e.target.value })}
                  placeholder="team@phizoquarry.com" />
              </Field>
              <Field label="App Password (16 characters)"
                hint="Generate at myaccount.google.com/apppasswords. Leave blank to keep current.">
                <Input type="password" value={s.smtp_password || ""} data-testid="setting-smtp-password"
                  onChange={(e) => setS({ ...s, smtp_password: e.target.value })}
                  placeholder="••••  ••••  ••••  ••••" />
              </Field>
            </div>
            <div className="text-xs text-[#a1a1aa] mt-3 leading-relaxed">
              Tip: if you're authenticating as <span className="mono">luckymccallum@gmail.com</span> but want
              emails to appear to come from <span className="mono">team@phizoquarry.com</span>,
              add <span className="mono">team@phizoquarry.com</span> as a verified
              <em> "Send mail as"</em> alias inside that Gmail account first. Otherwise Gmail will rewrite the
              visible From back to the authenticated address (Reply-To still routes to your sender).
            </div>
          </div>

          <div className="flex justify-between items-center pt-4 mt-4 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={sendTest} disabled={testing} data-testid="setting-test-email">
              <PaperPlaneTilt size={14} weight="bold" className="inline mr-1.5" />
              {testing ? "Sending test…" : `Send test to ${user.email}`}
            </BtnSecondary>
            <BtnPrimary type="submit" disabled={saving} data-testid="setting-save">
              {saving ? "Saving…" : "Save Email Settings"}
            </BtnPrimary>
          </div>
        </form>
      </div>
      )}

      {tab === "brand" && isAdmin && (
      <div className="phizo-card p-6" data-testid="brand-card">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 bg-[#FFC000] text-[#09090b] flex items-center justify-center">
            <Megaphone size={18} weight="bold" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-black tracking-tight">Brand & Marketing Copy</h2>
            <p className="text-xs text-[#52525b] mt-0.5">
              Editable vendor block shown on the Login page (Luroma section). Use this to update what other businesses see when they visit your sign-in screen.
            </p>
          </div>
        </div>

        {!brand ? (
          <div className="text-sm text-[#52525b]">Loading brand settings…</div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              setBrandSaving(true);
              try {
                const payload = { ...brand };
                if (industryDraft.trim()) {
                  payload.industries = [...(payload.industries || []), industryDraft.trim()];
                  setIndustryDraft("");
                }
                const { data } = await api.put("/settings/brand", payload);
                setBrand(data);
                toast.success("Brand settings saved");
              } catch (e2) { toast.error(formatError(e2)); }
              finally { setBrandSaving(false); }
            }}
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Vendor name" hint="Shown as 'Powered by ...' on the Login page.">
                <Input value={brand.vendor_name || ""} data-testid="brand-vendor-name"
                  onChange={(e) => setBrand({ ...brand, vendor_name: e.target.value })} />
              </Field>
              <Field label="Tagline (one line)">
                <Input value={brand.vendor_tagline || ""} data-testid="brand-tagline"
                  onChange={(e) => setBrand({ ...brand, vendor_tagline: e.target.value })} />
              </Field>
              <div className="sm:col-span-2">
                <Field label="About / pitch" hint="2–3 sentences. Will be rendered with line breaks preserved.">
                  <Textarea value={brand.vendor_about || ""} rows={4} data-testid="brand-about"
                    onChange={(e) => setBrand({ ...brand, vendor_about: e.target.value })} />
                </Field>
              </div>
              <Field label="Contact email">
                <Input type="email" value={brand.vendor_email || ""} data-testid="brand-email"
                  onChange={(e) => setBrand({ ...brand, vendor_email: e.target.value })}
                  placeholder="hello@yourcompany.com" />
              </Field>
              <Field label="Contact phone">
                <Input value={brand.vendor_phone || ""} data-testid="brand-phone"
                  onChange={(e) => setBrand({ ...brand, vendor_phone: e.target.value })}
                  placeholder="+260 77 168 6838" />
              </Field>
              <Field label="Website (optional)">
                <Input type="url" value={brand.vendor_website || ""} data-testid="brand-website"
                  onChange={(e) => setBrand({ ...brand, vendor_website: e.target.value })}
                  placeholder="https://luroma.co.zm" />
              </Field>
              <Field label="Call-to-action label">
                <Input value={brand.cta_label || ""} data-testid="brand-cta"
                  onChange={(e) => setBrand({ ...brand, cta_label: e.target.value })}
                  placeholder="Want a system for your business?" />
              </Field>
            </div>

            <div>
              <div className="phizo-label mb-2">Industries served</div>
              <div className="border border-[#e4e4e7] bg-white p-2 min-h-[48px] flex flex-wrap gap-1.5 items-center focus-within:border-[#002FA7]">
                {(brand.industries || []).map((t, i) => (
                  <span key={`${t}-${i}`} className="bg-[#FFF8E1] border border-[#FFC000] px-2 py-0.5 text-xs font-semibold flex items-center gap-1.5">
                    {t}
                    <button type="button" data-testid={`brand-industry-remove-${i}`}
                      onClick={() => setBrand({ ...brand, industries: brand.industries.filter((_, j) => j !== i) })}
                      className="text-[#a1a1aa] hover:text-[#FF2A2A]">
                      <X size={10} weight="bold" />
                    </button>
                  </span>
                ))}
                <input data-testid="brand-industry-input"
                  className="flex-1 min-w-[160px] outline-none text-sm px-1 py-1"
                  value={industryDraft}
                  onChange={(e) => setIndustryDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (["Enter", ","].includes(e.key)) {
                      e.preventDefault();
                      const v = industryDraft.trim();
                      if (v && !(brand.industries || []).includes(v)) {
                        setBrand({ ...brand, industries: [...(brand.industries || []), v] });
                      }
                      setIndustryDraft("");
                    } else if (e.key === "Backspace" && !industryDraft && (brand.industries || []).length) {
                      setBrand({ ...brand, industries: brand.industries.slice(0, -1) });
                    }
                  }}
                  placeholder={(brand.industries || []).length === 0 ? "Type and press Enter…" : ""} />
              </div>
              <div className="text-[10px] text-[#a1a1aa] mt-1">Press Enter or comma to add. Each tag shows as a chip on the Login screen.</div>
            </div>

            <div className="flex items-center gap-2 border-t border-[#e4e4e7] pt-3">
              <input id="brand-show-on-login" type="checkbox" data-testid="brand-show-on-login"
                checked={!!brand.show_on_login}
                onChange={(e) => setBrand({ ...brand, show_on_login: e.target.checked })}
                className="h-4 w-4" />
              <label htmlFor="brand-show-on-login" className="text-sm text-[#09090b]">
                Show the vendor block on the Login page
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
              <BtnSecondary type="button" onClick={loadBrand} disabled={brandSaving} data-testid="brand-revert">
                Discard changes
              </BtnSecondary>
              <BtnPrimary type="submit" disabled={brandSaving} data-testid="brand-save">
                {brandSaving ? "Saving…" : "Save Brand Settings"}
              </BtnPrimary>
            </div>
          </form>
        )}
      </div>
      )}

      {tab === "formulas" && canFormulas && (
        <div className="phizo-card p-6" data-testid="settings-formulas-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 bg-[#002FA7] text-white flex items-center justify-center">
              <FunctionIcon size={18} weight="bold" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-black tracking-tight">Payroll Formulas</h2>
              <p className="text-xs text-[#52525b] mt-0.5">
                Restricted to Admin &amp; Finance. Formulas run automatically when you click &ldquo;Run Payroll&rdquo;.
              </p>
            </div>
          </div>
          <PayrollFormulasPanel />
        </div>
      )}

      {tab === "data" && isAdmin && (
        <div className="phizo-card p-6" data-testid="settings-data-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 bg-[#002FA7] text-white flex items-center justify-center">
              <IdentificationCard size={18} weight="bold" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-black tracking-tight">Data Utilities</h2>
              <p className="text-xs text-[#52525b] mt-0.5">
                One-click, idempotent operations against the live database. Safe to re-run.
              </p>
            </div>
          </div>

          <div className="border border-[#e4e4e7] p-4 flex items-start gap-4">
            <div className="flex-1">
              <div className="font-bold">Sync Damstra IDs</div>
              <div className="text-xs text-[#52525b] mt-1">
                Applies the bundled <span className="mono">seed_data/damstra_ids.json</span> to
                every employee that is either missing an ID or has the wrong one. Refreshes any
                related draft payroll rows so payslip PDFs reflect the new IDs.
              </div>
              {damstraSync && (
                <div className="mt-3 text-xs mono bg-[#f4f4f5] p-2 border border-[#e4e4e7]" data-testid="damstra-sync-result">
                  <div><b>employees_updated:</b> {damstraSync.employees_updated}</div>
                  <div><b>payroll_rows_updated:</b> {damstraSync.payroll_rows_updated}</div>
                  {damstraSync.unmatched_employees?.length > 0 && (
                    <div>
                      <b>unmatched:</b> {damstraSync.unmatched_employees.join(", ")}
                    </div>
                  )}
                </div>
              )}
            </div>
            <BtnPrimary onClick={syncDamstraIds} disabled={damstraSyncing}
              data-testid="damstra-sync-btn">
              <IdentificationCard size={14} weight="bold" className="inline mr-1.5" />
              {damstraSyncing ? "Syncing…" : "Run Sync"}
            </BtnPrimary>
          </div>

          <div className="border border-[#e4e4e7] p-4 flex items-start gap-4 mt-4">
            <div className="flex-1">
              <div className="font-bold">Sync Monthly Tonnage</div>
              <div className="text-xs text-[#52525b] mt-1">
                Loads bundled <span className="mono">seed_data/tonnage_daily.json</span> — the
                declared daily crushed-tonnage report imported from the Excel workbook. This
                wipes prior imports (rows tagged Seed Import) and re-inserts a clean snapshot;
                user-entered production records are preserved.
              </div>
              {tonnageSync && (
                <div className="mt-3 text-xs mono bg-[#f4f4f5] p-2 border border-[#e4e4e7]" data-testid="tonnage-sync-result">
                  <div><b>inserted:</b> {tonnageSync.inserted}</div>
                  <div><b>removed_previous_import:</b> {tonnageSync.removed_previous_import}</div>
                  <div><b>skipped_notes:</b> {tonnageSync.skipped_notes}</div>
                  <div><b>total_source_rows:</b> {tonnageSync.total_source_rows}</div>
                </div>
              )}
            </div>
            <BtnPrimary onClick={syncTonnage} disabled={tonnageSyncing}
              data-testid="tonnage-sync-btn">
              <TruckIcon size={14} weight="bold" className="inline mr-1.5" />
              {tonnageSyncing ? "Syncing…" : "Run Sync"}
            </BtnPrimary>
          </div>
        </div>
      )}

      {tab === "products" && isAdmin && (
        <div className="phizo-card p-6" data-testid="settings-products-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 bg-[#00A142] text-white flex items-center justify-center">
              <PackageIcon size={18} weight="bold" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-black tracking-tight">Crush Products</h2>
              <p className="text-xs text-[#52525b] mt-0.5">
                Add, edit or archive the product sizes Phizo crushes. Rates feed the
                Production, Delivery Note and Invoice modules. Admin-only.
              </p>
            </div>
          </div>
          <CrushProductsPanel />
        </div>
      )}

      {tab === "cost_centers" && isAdmin && (
        <div className="phizo-card p-6" data-testid="settings-cost-centers-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 bg-[#002FA7] text-white flex items-center justify-center"><MapPin size={18} weight="bold" /></div>
            <div>
              <h2 className="font-heading text-xl font-black tracking-tight">Cost Centers</h2>
              <p className="text-xs text-[#52525b] mt-0.5">Operational sub-units — Load and Haul, Drill and Blast, Crusher, etc.</p>
            </div>
          </div>
          <LookupsPanel kind="cost_center" />
        </div>
      )}

      {tab === "cost_categories" && isAdmin && (
        <div className="phizo-card p-6" data-testid="settings-cost-categories-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 bg-[#FFC000] text-[#09090b] flex items-center justify-center"><TagIcon size={18} weight="bold" /></div>
            <div>
              <h2 className="font-heading text-xl font-black tracking-tight">Cost Categories</h2>
              <p className="text-xs text-[#52525b] mt-0.5">Budget classification — Labour, Maintenance, Operating Materials…</p>
            </div>
          </div>
          <LookupsPanel kind="cost_category" />
        </div>
      )}

      {tab === "departments" && isAdmin && (
        <div className="phizo-card p-6" data-testid="settings-departments-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 bg-[#5B21B6] text-white flex items-center justify-center"><Buildings size={18} weight="bold" /></div>
            <div>
              <h2 className="font-heading text-xl font-black tracking-tight">Departments</h2>
              <p className="text-xs text-[#52525b] mt-0.5">Assigned to every employee & user; drives the HR org chart.</p>
            </div>
          </div>
          <LookupsPanel kind="department" />
        </div>
      )}

      {tab === "service_categories" && isAdmin && (
        <div className="phizo-card p-6" data-testid="settings-service-categories-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 bg-[#FF6B00] text-white flex items-center justify-center"><Wrench size={18} weight="bold" /></div>
            <div>
              <h2 className="font-heading text-xl font-black tracking-tight">Service Categories</h2>
              <p className="text-xs text-[#52525b] mt-0.5">Maintenance service types (250Hr, 500Hr, 1000Hr…) with recurrence hours.</p>
            </div>
          </div>
          <LookupsPanel kind="service_category" />
        </div>
      )}

      {tab === "appearance" && (
        <div className="phizo-card p-6" data-testid="settings-appearance-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 bg-[#09090b] text-white flex items-center justify-center">
              {isDark ? <Sun size={18} weight="bold" /> : <Moon size={18} weight="bold" />}
            </div>
            <div>
              <h2 className="font-heading text-xl font-black tracking-tight">Appearance</h2>
              <p className="text-xs text-[#52525b] mt-0.5">
                Toggle Day / Night mode. Your choice is remembered on this device.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setMode("light")}
              data-testid="theme-light"
              className={`border p-4 min-w-[180px] text-left transition-colors ${isDark ? "border-[#e4e4e7] hover:border-[#002FA7]" : "border-[#002FA7] bg-[#002FA7] text-white"}`}
            >
              <Sun size={18} weight="bold" />
              <div className="font-bold mt-2">Day Mode</div>
              <div className="text-xs opacity-70">Bright & high-contrast — best for daytime.</div>
            </button>
            <button
              type="button"
              onClick={() => setMode("dark")}
              data-testid="theme-dark"
              className={`border p-4 min-w-[180px] text-left transition-colors ${isDark ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-[#e4e4e7] hover:border-[#002FA7]"}`}
            >
              <Moon size={18} weight="bold" />
              <div className="font-bold mt-2">Night Mode</div>
              <div className="text-xs opacity-70">Muted dark surfaces — easier on the eyes at night.</div>
            </button>
          </div>
        </div>
      )}

      {tab === "email" && isAdmin && (
      <div className="phizo-card p-6" data-testid="email-logs">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-[#09090b] text-white flex items-center justify-center">
              <Clock size={18} weight="bold" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-black tracking-tight">Email activity</h2>
              <p className="text-xs text-[#52525b] mt-0.5">Most recent 30 emails sent from this system.</p>
            </div>
          </div>
          <BtnSecondary onClick={loadLogs} type="button">Refresh</BtnSecondary>
        </div>
        {logs.length === 0 ? (
          <Empty label="No emails have been sent yet." />
        ) : (
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr>
                  <th>Status</th><th>When</th><th>To</th><th>Subject</th><th>Category</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id}>
                    <td>
                      {l.status === "sent" ? (
                        <span className="inline-flex items-center gap-1 text-[#00A142] text-xs font-semibold">
                          <CheckCircle size={14} weight="fill" /> Sent
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#FF2A2A] text-xs font-semibold" title={l.error || ""}>
                          <XCircle size={14} weight="fill" /> Failed
                        </span>
                      )}
                    </td>
                    <td className="text-xs text-[#52525b] mono">
                      {new Date(l.sent_at).toLocaleString("en-GB")}
                    </td>
                    <td className="mono text-xs">{l.to.join(", ")}</td>
                    <td className="text-sm">{l.subject}</td>
                    <td className="text-xs text-[#52525b]">{l.category}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
